import React, { useState } from "react";
import {
    Box,
    Backdrop,
    CircularProgress,
    Snackbar,
    Alert,
    Tabs,
    Tab
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import API from "../../../../API";
import BrandEmailDialog from "./brandEmailDialog";
import CreatorEmailsDialog from "./partnershipSuite/creatorEmailsDialog";
import ClassificationManagementTab from './partnershipSuite/ClassificationManagementTab';
import EmailDebugDialog from './partnershipSuite/EmailDebugDialog';
import EmailHistoryTab from "./partnershipSuite/emailhistorytab";
import PartnerManagementTab from "./partnershipSuite/PartnerManagementTab";
import ManualProcessingTab from "./partnershipSuite/manualprocessingtab";
import ProcessingResultsDialog from "./partnershipSuite/processingresultsdialog";
import {
    Email,
    CheckCircle,
    PlayArrow,
    Assessment
} from "@mui/icons-material";
import InterestingLeadsTab from './partnershipSuite/InterestingLeadsTab';
import { StarBorder } from "@mui/icons-material"; // Add icon

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index} style={{ paddingTop: 16 }}>
            {value === index && children}
        </div>
    );
}

export default function BrandEmails() {
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('error');
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [processingResults, setProcessingResults] = useState(null);
    const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
    const [resultsType, setResultsType] = useState('');
    
    const [creatorEmailsDialogOpen, setCreatorEmailsDialogOpen] = useState(false);
    const [selectedCreatorUsername, setSelectedCreatorUsername] = useState(null);
    
    const [debugDialogOpen, setDebugDialogOpen] = useState(false);
    const [debugCreator, setDebugCreator] = useState(null);
    
    const queryClient = useQueryClient();

    const showToast = (message, severity = 'error') => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setToastOpen(false);
    };

    const { data: keywords, error: keywordsErrors, isLoading: keywordsLoading } = useQuery({
        queryKey: ['creatorEmailsKeywords'],
        queryFn: API.creators.adminBrandKeywords,
        staleTime: 1000.0 * 60 * 60,
        refetchOnWindowFocus: false,
    });

    const checkEmailsMutation = useMutation(
        (params) => API.blitzAI.checkEmails(params),
        {
            onSuccess: (data) => {
                setProcessingResults(data);
                setResultsType('check');
                setResultsDialogOpen(true);
                showToast('Email check completed successfully', 'success');
                queryClient.invalidateQueries(['partnerStatus']);
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to check emails');
            }
        }
    );

    const processEmailsMutation = useMutation(
        (params) => API.blitzAI.processEmails(params),
        {
            onSuccess: (data) => {
                setProcessingResults(data);
                setResultsType('process');
                setResultsDialogOpen(true);
                showToast(`Email processing completed. ${data.total_emails_sent} emails sent.`, 'success');
                queryClient.invalidateQueries(['partnerStatus']);
                queryClient.invalidateQueries(['creatorEmails']);
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to process emails');
            }
        }
    );

    const processFollowupsMutation = useMutation(
        (params) => API.blitzAI.processFollowups(params),
        {
            onSuccess: (data) => {
                setProcessingResults(data);
                setResultsType('followup');
                setResultsDialogOpen(true);
                showToast(`Follow-up processing completed. ${data.total_followups_sent} follow-ups sent.`, 'success');
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to process follow-ups');
            }
        }
    );

    const processForwardedMutation = useMutation(
        (params) => API.blitzAI.processForwarded(params),
        {
            onSuccess: (data) => {
                setProcessingResults(data);
                setResultsType('forwarded');
                setResultsDialogOpen(true);
                showToast(`Forwarded emails processed. ${data.messages_handled} messages handled.`, 'success');
                queryClient.invalidateQueries(['creatorEmails']);
            },
            onError: (error) => {
                showToast(error.response?.data?.error || 'Failed to process forwarded emails');
            }
        }
    );

    const onCloseDialog = () => {
        setSelectedEmail(null);
    };

    const handlePartnerProcess = (usernames, type) => {
        const params = {
            creator_usernames: usernames,
            dry_run: true // Always dry run from partner cards
        };

        switch(type) {
            case 'check':
                checkEmailsMutation.mutate(params);
                break;
            case 'process':
                processEmailsMutation.mutate(params);
                break;
            default:
                break;
        }
    };

    const handleBulkAction = (action, dryRun) => {
        const params = {
            [action === 'check' ? 'check_all' : 'process_all']: true,
            dry_run: dryRun
        };

        switch(action) {
            case 'check':
                checkEmailsMutation.mutate(params);
                break;
            case 'process':
                processEmailsMutation.mutate(params);
                break;
            case 'followup':
                processFollowupsMutation.mutate(params);
                break;
            case 'forwarded':
                processForwardedMutation.mutate(params);
                break;
            default:
                break;
        }
    };

    const isProcessing = checkEmailsMutation.isLoading || 
                        processEmailsMutation.isLoading || 
                        processFollowupsMutation.isLoading ||
                        processForwardedMutation.isLoading;

    const handleViewCreatorEmails = (username) => {
        setSelectedCreatorUsername(username);
        setCreatorEmailsDialogOpen(true);
    };

    const handleCreatorEmailsDialogClose = () => {
        setCreatorEmailsDialogOpen(false);
        setSelectedCreatorUsername(null);
    };

    const handleCreatorEmailClick = (emailId) => {
        setSelectedEmail(emailId);
    };

    const handleOpenDebug = (creator) => {
        setDebugCreator(creator);
        setDebugDialogOpen(true);
    };

    const handleCloseDebug = () => {
        setDebugDialogOpen(false);
        setDebugCreator(null);
    };

    return (
        <Box sx={{ padding: 2 }}>
            <Backdrop 
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} 
                open={(keywordsLoading || isProcessing) && !keywordsErrors}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Snackbar
                open={toastOpen}
                autoHideDuration={6000}
                onClose={handleClose}
            >
                <Alert severity={toastSeverity} variant='filled' sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Email History" icon={<Email />} iconPosition="start" />
                    <Tab label="Partner Management" icon={<CheckCircle />} iconPosition="start" />
                    <Tab label="Manual Processing" icon={<PlayArrow />} iconPosition="start" />
                    <Tab label="Classification Tools" icon={<Assessment />} iconPosition="start" />
                    <Tab label="Interesting Leads" icon={<StarBorder />} iconPosition="start" /> {/* NEW TAB */}

                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                <EmailHistoryTab
                    keywords={keywords}
                    onEmailClick={setSelectedEmail}
                    showToast={showToast}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                <PartnerManagementTab
                    onPartnerProcess={handlePartnerProcess}
                    onViewEmails={handleViewCreatorEmails}
                    onDebug={handleOpenDebug}
                    showToast={showToast}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                <ManualProcessingTab
                    onBulkAction={handleBulkAction}
                    isProcessing={isProcessing}
                />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
                <ClassificationManagementTab showToast={showToast} />
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
                <InterestingLeadsTab showToast={showToast} />
            </TabPanel>
            <BrandEmailDialog 
                email={selectedEmail} 
                keywords={keywords?.keywords_mapping} 
                onClose={onCloseDialog}
            />

            <ProcessingResultsDialog
                open={resultsDialogOpen}
                onClose={() => setResultsDialogOpen(false)}
                results={processingResults}
                type={resultsType}
            />

            <CreatorEmailsDialog
                open={creatorEmailsDialogOpen}
                onClose={handleCreatorEmailsDialogClose}
                username={selectedCreatorUsername}
                onEmailClick={handleCreatorEmailClick}
            />

            <EmailDebugDialog
                open={debugDialogOpen}
                onClose={handleCloseDebug}
                username={debugCreator?.username}
                creatorEmail={debugCreator?.email}
                showToast={showToast}
            />
        </Box>
    );
}